import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { Star, Quote, Youtube, ArrowRight, PlayCircle, Volume2, VolumeX } from "lucide-react";

function ytId(url) {
  if (!url || typeof url !== "string") return null;
  const m = url.match(/(?:youtube\.com\/(?:.*v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? m[1] : null;
}

// Demo video testimonials shown when admin has not yet added any real video testimonials.
// Uses royalty-free mixkit MP4s of business teams — admin can replace via /admin/avis.
const DEMO_VIDEOS = [
  {
    id: "demo-v1",
    author_name: "Camille D.",
    company: "Co-fondatrice — Studio créatif",
    quote: "L'accompagnement Besson a transformé notre vision en business concret.",
    mp4: "https://assets.mixkit.co/videos/4818/4818-720.mp4",
    poster: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?crop=entropy&cs=srgb&fm=jpg&w=1280&q=70",
  },
  {
    id: "demo-v2",
    author_name: "Karim S.",
    company: "Directeur — Cabinet de conseil",
    quote: "Méthode claire, outils efficaces, ROI mesurable en quelques mois.",
    mp4: "https://assets.mixkit.co/videos/22815/22815-720.mp4",
    poster: "https://images.unsplash.com/photo-1573164713988-8665fc963095?crop=entropy&cs=srgb&fm=jpg&w=1280&q=70",
  },
];

export default function TestimonialsSection({ showTestimonials = true, showGoogleReviews = true }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data || [])).catch(() => setItems([]));
  }, []);

  const textItems = items.length === 0 ? [
    { id: "f1", author_name: "Marie L.", company: "Dirigeante PME e-commerce", content: "En 3 mois, on a structuré notre force commerciale et on a doublé nos leads qualifiés. Bluffant.", rating: 5, source: "manuel" },
    { id: "f2", author_name: "Thomas R.", company: "Porteur de projet SaaS", content: "Le pack identité + le MVP livré en 6 semaines m'ont permis de signer mes 3 premiers clients pilotes.", rating: 5, source: "manuel" },
    { id: "f3", author_name: "Julien B.", company: "DG groupe industriel", content: "Le dashboard sur-mesure a transformé notre pilotage. ROI immédiat sur les coûts opérationnels.", rating: 5, source: "manuel" },
  ] : items;

  // Apply admin visibility filters
  const filteredTextItems = textItems.filter((t) => {
    const isGoogle = t.source === "google";
    if (isGoogle) return showGoogleReviews;
    return showTestimonials;
  });
  const filteredVideoItems = showTestimonials ? items.filter((t) => t.youtube_url && ytId(t.youtube_url)) : [];
  const showDemoVideos = filteredVideoItems.length === 0 && showTestimonials;

  // If nothing to show after filtering, render nothing
  if (filteredTextItems.length === 0 && filteredVideoItems.length === 0 && !showDemoVideos) return null;

  return (
    <section id="avis" data-testid="testimonials-section" className="py-20 sm:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="max-w-2xl">
            <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#06B6D4]">Témoignages clients</div>
            <h2 data-testid="testimonials-title" className="display mt-3 text-3xl sm:text-5xl text-[#0A1128] leading-[1.05] font-extrabold">
              Ils nous font confiance,{" "}
              <em className="not-italic relative">
                <span className="relative z-10">et vous ?</span>
                <span className="absolute inset-x-0 bottom-1 h-3 bg-[#A7F3D0] -z-0 rounded-sm"></span>
              </em>
            </h2>
            <p className="mt-3 text-[#475569] text-sm sm:text-base">Des entrepreneurs, dirigeants et porteurs de projets partagent leurs résultats.</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <div className="flex items-center gap-1">
              {[...Array(5)].map((_, i) => <Star key={i} size={20} className="fill-[#FFDCA8] stroke-[#FFDCA8]" />)}
            </div>
            <div className="text-sm">
              <div className="display font-extrabold text-[#0A1128]">5.0 / 5</div>
              <div className="text-xs text-[#64748B]">{items.length > 0 ? `${items.length} avis` : "Démo"}</div>
            </div>
          </div>
        </div>

        {/* Video testimonials — mis en évidence */}
        {showTestimonials && (
        <div className="mt-12">
          <div className="flex items-center gap-2 mb-5">
            <PlayCircle size={20} className="text-[#06B6D4]" />
            <h3 className="display text-lg sm:text-xl text-[#0A1128] font-bold">Témoignages vidéo</h3>
            {showDemoVideos && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FFDCA8]/40 border border-[#FFDCA8] text-[#92400E] font-bold">démo</span>}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {showDemoVideos
              ? DEMO_VIDEOS.map((v) => <VideoCard key={v.id} v={v} />)
              : filteredVideoItems.map((t) => (
                  <div key={t.id} data-testid={`testimonial-video-${t.id}`} className="rounded-2xl overflow-hidden border border-black/8 bg-white shadow-sm hover:shadow-xl transition-shadow">
                    <div className="aspect-video bg-black">
                      <iframe
                        title={t.author_name}
                        src={`https://www.youtube.com/embed/${ytId(t.youtube_url)}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                      />
                    </div>
                    <div className="p-5">
                      <p className="text-[#0A1128] leading-relaxed text-[15px]">{t.content}</p>
                      <div className="mt-4 flex items-center justify-between">
                        <div>
                          <div className="display font-bold text-[#0A1128]">{t.author_name}</div>
                          <div className="text-xs text-[#64748B]">{t.company}</div>
                        </div>
                        <Youtube size={18} className="text-[#FF0000]" />
                      </div>
                    </div>
                  </div>
                ))}
          </div>
        </div>
        )}

        {/* Text testimonials grid */}
        {filteredTextItems.length > 0 && (
        <div className="mt-14">
          <div className="flex items-center gap-2 mb-5">
            <Quote size={18} className="text-[#06B6D4]" />
            <h3 className="display text-lg sm:text-xl text-[#0A1128] font-bold">Ce qu'ils en disent</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredTextItems.slice(0, 6).map((t) => (
              <div key={t.id} data-testid={`testimonial-${t.id}`} className="relative rounded-3xl p-6 bg-[#FAFAFA] border border-black/5 hover:shadow-xl transition-shadow">
                <Quote size={28} className="text-[#06B6D4] opacity-50" />
                <p className="mt-3 text-[#0A1128] leading-relaxed text-[15px]">{t.content}</p>
                <div className="mt-5 flex items-center justify-between">
                  <div>
                    <div className="display font-bold text-[#0A1128]">{t.author_name}</div>
                    <div className="text-xs text-[#64748B]">{t.company || "Client"}</div>
                  </div>
                  <div className="flex">
                    {[...Array(t.rating || 5)].map((_, i) => <Star key={i} size={14} className="fill-[#FFDCA8] stroke-[#FFDCA8]" />)}
                  </div>
                </div>
                {t.source === "google" && (
                  <span className="absolute top-4 right-4 text-[10px] px-2 py-0.5 rounded-full bg-white border border-black/5 font-bold text-[#0A1128]">Google</span>
                )}
              </div>
            ))}
          </div>
        </div>
        )}

        {/* CTA */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3 text-center">
          <Link
            to="/#decouverte"
            data-testid="testimonials-cta"
            className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#0A1128] hover:bg-[#06B6D4] text-white font-extrabold text-[15px] shadow-xl transition-colors"
          >
            Rejoignez-les — RDV découverte offert
            <ArrowRight size={16} className="group-hover:translate-x-0.5 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function VideoCard({ v }) {
  const ref = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleSound = () => {
    const el = ref.current;
    if (!el) return;
    const next = !muted;
    el.muted = next;
    if (!next) {
      // Try to play with sound; if blocked, re-mute silently
      el.play().catch(() => {
        el.muted = true;
        setMuted(true);
      });
    }
    setMuted(next);
  };

  return (
    <div data-testid={`testimonial-video-${v.id}`} className="rounded-2xl overflow-hidden border border-black/8 bg-white shadow-sm hover:shadow-xl transition-shadow">
      <div className="aspect-video bg-black relative group">
        <video
          ref={ref}
          className="w-full h-full object-cover cursor-pointer"
          autoPlay
          muted
          loop
          playsInline
          poster={v.poster}
          onClick={toggleSound}
        >
          <source src={v.mp4} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
        <Quote size={28} className="absolute top-4 left-4 text-white/80 pointer-events-none" />
        {/* Sound toggle */}
        <button
          type="button"
          onClick={toggleSound}
          data-testid={`testimonial-video-${v.id}-sound`}
          aria-label={muted ? "Activer le son" : "Couper le son"}
          className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/60 hover:bg-[#06B6D4] text-white text-[11px] font-bold backdrop-blur-sm border border-white/20 transition-colors"
        >
          {muted ? <><VolumeX size={12} /> Activer le son</> : <><Volume2 size={12} /> Muet</>}
        </button>
      </div>
      <div className="p-5">
        <p className="text-[#0A1128] leading-relaxed text-[15px] italic">« {v.quote} »</p>
        <div className="mt-4 flex items-center justify-between">
          <div>
            <div className="display font-bold text-[#0A1128]">{v.author_name}</div>
            <div className="text-xs text-[#64748B]">{v.company}</div>
          </div>
          <div className="flex">
            {[...Array(5)].map((_, i) => <Star key={i} size={14} className="fill-[#FFDCA8] stroke-[#FFDCA8]" />)}
          </div>
        </div>
      </div>
    </div>
  );
}
